import { _isNotBlank } from '@/littledash';
import type { MetadataFieldTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import type { StudyOverviewSectionV1 } from '@/model/Study.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { web } from '@/support/route';

import type { OverviewGroup, OverviewReferences, OverviewSectionKey } from './OverviewAccordion.model';

const SECTION_TITLES: Record<OverviewSectionKey, string> = {
  metadata: 'Metadata',
  groups: 'Groups',
  diseases: 'Diseases',
  treatments: 'Treatments',
  sampling_plan: 'Sampling plan',
  tasks: 'Tasks',
  preset: 'Preset',
  alerts: 'Alerts',
};

/**
 * Metadata form sections carry their own title, and the preset section its preset's title when it
 * has one; every other section uses a fixed one.
 */
export const overviewSectionTitle = (section: StudyOverviewSectionV1): string => {
  if (section.status === 'success' && section.type === 'metadata') {
    return section.title;
  }
  if (section.status === 'success' && section.type === 'preset' && _isNotBlank(section.title)) {
    return section.title;
  }
  return SECTION_TITLES[section.type];
};

export const isEmptyOverviewSection = (section: StudyOverviewSectionV1): boolean =>
  section.status === 'success' && section.count === 0;

/**
 * Stable identifiers for each section, in the order given. Metadata may repeat,
 * so each metadata section is numbered by its position among metadata sections.
 */
export const overviewSectionIds = (sections: Array<StudyOverviewSectionV1>): Array<string> =>
  sections.reduce<{ ids: Array<string>; metadataCount: number }>(
    (acc, section) => {
      if (section.type === 'metadata') {
        acc.ids.push(`metadata-${acc.metadataCount}`);
        acc.metadataCount += 1;
      } else {
        acc.ids.push(section.type);
      }
      return acc;
    },
    { ids: [], metadataCount: 0 }
  ).ids;

/**
 * Existing page that manages this section.
 */
export const overviewSectionManagePath = (sectionKey: OverviewSectionKey, studyId: string): string => {
  switch (sectionKey) {
    case 'groups':
    case 'diseases':
    case 'treatments':
      return web('studies.studyGroups.tab', { id: studyId, tab: sectionKey });
    case 'sampling_plan':
      return web('studies.samples', { id: studyId }, { tab: 'plan' });
    case 'tasks':
      return web('studies.settings', { id: studyId }, { step: 'tasks' });
    case 'preset':
      return web('studies.settings', { id: studyId }, { step: 'preset' });
    case 'alerts':
      return web('studies.settings', { id: studyId }, { step: 'alerts' });
    case 'metadata':
      return web('studies.settings', { id: studyId }, { step: 'metadata' });
  }
};

type OverviewGroupField = OverviewGroup['metadata'][number];

const DEFAULT_GROUP_COLOR = '#333333';
const METADATA_FIELD_TYPES: ReadonlyArray<MetadataFieldTypes> = [
  'text',
  'numeric',
  'date',
  'select',
  'multi_select',
  'lookup_select',
  'lookup_multi_select',
];

const isMetadataFieldType = (value: string): value is MetadataFieldTypes =>
  METADATA_FIELD_TYPES.some((fieldType) => fieldType === value);

const toGroupMetadata = (field: OverviewGroupField): MetadataFieldWithValue => ({
  id: field.glossary_api_id,
  created_at: '',
  glossary_id: field.glossary_api_id,
  title: field.title,
  field_type: isMetadataFieldType(field.field_type) ? field.field_type : 'text',
  slug: field.slug,
  value: field.value,
  read_only: false,
  required: false,
  options: field.options,
});

/**
 * Maps compact overview groups into the TreatmentGroup shape used by ReviewTable
 * and the task target details modal.
 */
export const toTreatmentGroup = (group: OverviewGroup, index: number): TreatmentGroup => ({
  id: group.api_id,
  api_id: group.api_id,
  created_at: '',
  name: group.name,
  no: group.no ?? index + 1,
  color: group.color ?? DEFAULT_GROUP_COLOR,
  max_subjects: group.max_subjects ?? 0,
  control: group.control,
  metadata: group.metadata.map(toGroupMetadata),
});

/**
 * Indexes the groups and treatment/disease rows of a sections response by api_id
 * so sections that only carry ids can resolve them. Failed sections contribute nothing.
 */
export const buildOverviewReferences = (sections: Array<StudyOverviewSectionV1>): OverviewReferences =>
  sections.reduce<OverviewReferences>(
    (references, section) => {
      if (section.status !== 'success') {
        return references;
      }
      if (section.type === 'groups') {
        section.rows.forEach((group, index) =>
          references.groupsByApiId.set(group.api_id, toTreatmentGroup(group, index))
        );
      }
      if (section.type === 'treatments' || section.type === 'diseases') {
        section.rows.forEach((row) => references.treatmentsByApiId.set(row.api_id, { row, sectionType: section.type }));
      }
      return references;
    },
    { groupsByApiId: new Map(), treatmentsByApiId: new Map() }
  );
